import {
  Captions,
  CaptionsOff,
  ChevronDown,
  Headphones,
  Languages,
  Mic,
  MicOff,
  RefreshCw,
  Settings2,
  Square,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import type { RealtimeTranslationStatus } from "../lib/realtimeTranslation";
import { applyDocumentMetadata } from "../shared/documentMetadata";
import type { TargetLanguage } from "../shared/languages";
import { UI_COPY } from "../shared/uiCopy";
import { AudioDeviceSettings, type AudioDeviceSettingsProps } from "./AudioDeviceSettings";
import { StatusBadge } from "./StatusBadge";

export type ListeningScreenProps = {
  language: TargetLanguage;
  transcript: string;
  status: RealtimeTranslationStatus;
  microphoneEnabled: boolean;
  audioEnabled: boolean;
  textVisible: boolean;
  volume: number;
  devices: AudioDeviceSettingsProps;
  errorMessage?: string | null;
  recordingActive?: boolean;
  supplement?: ReactNode;
  onMicrophoneEnabledChange(value: boolean): void;
  onAudioEnabledChange(value: boolean): void;
  onTextVisibleChange(value: boolean): void;
  onVolumeChange(value: number): void;
  onChangeLanguage(): void;
  onLeave(): void;
  onRetry(): void;
};

export function ListeningScreen({
  language,
  transcript,
  status,
  microphoneEnabled,
  audioEnabled,
  textVisible,
  volume,
  devices,
  errorMessage,
  recordingActive = false,
  supplement,
  onMicrophoneEnabledChange,
  onAudioEnabledChange,
  onTextVisibleChange,
  onVolumeChange,
  onChangeLanguage,
  onLeave,
  onRetry,
}: ListeningScreenProps) {
  const copy = UI_COPY[language];
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    return applyDocumentMetadata(language);
  }, [language]);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const transcriptScroller = transcriptScrollRef.current;
      const bottomSentinel = bottomSentinelRef.current;
      if (!transcriptScroller || !bottomSentinel) return;

      transcriptScroller.scrollTo({
        top: bottomSentinel.offsetTop + bottomSentinel.offsetHeight,
        behavior: reduceMotion ? "auto" : "smooth",
      });
    });

    return () => cancelAnimationFrame(frameId);
  }, [transcript]);

  return (
    <main className="listening-shell">
      <header className="session-header">
        <div className="product-mark product-mark--compact">
          <span className="product-mark__icon" aria-hidden="true">
            <Captions size={19} strokeWidth={1.8} />
          </span>
          <span>{copy.appName}</span>
        </div>
        <div className="session-header__actions">
          <StatusBadge language={language} status={status} />
          {status === "error" && (
            <button className="secondary-button secondary-button--compact" type="button" onClick={onRetry}>
              <RefreshCw size={15} aria-hidden="true" />
              {copy.retry}
            </button>
          )}
        </div>
      </header>

      {errorMessage && <p className="error-message" role="alert">{errorMessage}</p>}

      {supplement}

      <section className="translation-ribbon" data-status={status} aria-labelledby="translation-title">
        <div className="translation-ribbon__rail" aria-hidden="true">
          <span />
        </div>
        <div className="translation-ribbon__body">
          <div className="translation-ribbon__heading">
            <p className="eyebrow" id="translation-title">{copy.liveTranslation}</p>
            <span className="translation-ribbon__language">
              {language.toUpperCase()}
            </span>
          </div>
          <div
            ref={transcriptScrollRef}
            className="translation-ribbon__scroll"
            style={{ overflowY: "auto" }}
            role="log"
            aria-label={copy.liveTranslation}
            aria-live="polite"
            aria-relevant="additions text"
          >
            {textVisible && (
              <p className={transcript ? "transcript" : "transcript transcript--empty"}>
                {transcript || copy.transcriptEmpty}
              </p>
            )}
            <span ref={bottomSentinelRef} aria-hidden="true" />
          </div>
        </div>
      </section>

      <section className="instrument-dock" aria-label={copy.audioDevices}>
        <div className="instrument-dock__toggles">
          <button
            className="instrument-button"
            type="button"
            aria-pressed={microphoneEnabled}
            aria-label={microphoneEnabled ? copy.muteMicrophone : copy.unmuteMicrophone}
            disabled={recordingActive}
            onClick={() => onMicrophoneEnabledChange(!microphoneEnabled)}
          >
            {microphoneEnabled ? <Mic size={18} aria-hidden="true" /> : <MicOff size={18} aria-hidden="true" />}
            <span>{microphoneEnabled ? copy.microphone : copy.unmuteMicrophone}</span>
          </button>
          <button
            className="instrument-button"
            type="button"
            aria-pressed={audioEnabled}
            aria-label={audioEnabled ? copy.turnAudioOff : copy.turnAudioOn}
            onClick={() => onAudioEnabledChange(!audioEnabled)}
          >
            {audioEnabled ? <Volume2 size={18} aria-hidden="true" /> : <VolumeX size={18} aria-hidden="true" />}
            <span>{audioEnabled ? copy.audioOutput : copy.turnAudioOn}</span>
          </button>
          <button
            className="instrument-button"
            type="button"
            aria-pressed={textVisible}
            aria-label={textVisible ? copy.hideText : copy.showText}
            onClick={() => onTextVisibleChange(!textVisible)}
          >
            {textVisible ? <Captions size={18} aria-hidden="true" /> : <CaptionsOff size={18} aria-hidden="true" />}
            <span>{textVisible ? copy.liveTranslation : copy.showText}</span>
          </button>
        </div>

        <label className="volume-control">
          <Headphones size={17} aria-hidden="true" />
          <span>{copy.volume}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            aria-label={copy.volume}
            onChange={(event) => onVolumeChange(Number(event.currentTarget.value))}
          />
        </label>

        <details className="device-settings device-settings--dock">
          <summary>
            <span>
              <Settings2 size={17} aria-hidden="true" />
              {copy.audioDevices}
              <ChevronDown className="disclosure-icon" size={17} aria-hidden="true" />
            </span>
          </summary>
          <AudioDeviceSettings language={language} {...devices} inputDisabled={recordingActive} />
        </details>

        <div className="instrument-dock__session-actions">
          <button className="text-button" type="button" onClick={onChangeLanguage}>
            <Languages size={17} aria-hidden="true" />
            {copy.changeLanguage}
          </button>
          <button className="text-button text-button--leave" type="button" onClick={onLeave}>
            <Square size={15} aria-hidden="true" />
            {copy.stopListening}
          </button>
        </div>
      </section>
    </main>
  );
}
